import { Link } from 'react-router-dom';
import { Award, Target, TrendingUp, Briefcase, CheckCircle, ArrowRight, BookOpen, Video, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { erpPrograms } from '@/lib/api';

const careerPaths = [
  {
    level: 'Entry Level',
    roles: ['ERP Support Analyst', 'Junior Consultant', 'Implementation Associate', 'Business Analyst'],
    salary: '$50K - $75K',
    experience: '0-2 years'
  },
  {
    level: 'Mid Level',
    roles: ['ERP Consultant', 'Functional Analyst', 'Technical Consultant', 'Module Lead'],
    salary: '$75K - $110K',
    experience: '2-5 years'
  },
  {
    level: 'Senior Level',
    roles: ['Senior Consultant', 'Solution Architect', 'Project Manager', 'Practice Lead'],
    salary: '$110K - $150K+',
    experience: '5+ years'
  },
];

const certificationBenefits = [
  'Industry-recognized credentials',
  'Higher salary potential',
  'Better job opportunities',
  'Professional credibility',
  'Competitive advantage',
  'Global career mobility',
];

export default function CertificationPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="certification-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              
              
            >
              <span className="inline-block px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-6">
                Certification & Career Outcomes
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-6">
                Advance Your Career With ERP Certification
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Our training programs are aligned with official certification exams, 
                preparing you for industry-recognized credentials that employers value.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/programs">
                  <Button className="btn-primary" data-testid="start-certification-btn">
                    Start Your Certification Journey
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="btn-secondary">
                    Career Consultation
                  </Button>
                </Link>
              </div>
            </div>
            
            <div
              
              
              
            >
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-brand-700" />
                    <span>Why Get Certified?</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {certificationBenefits.map((benefit, i) => (
                      <li key={i} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-brand-500" />
                        <span className="text-slate-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Paths by ERP */}
      <section className="py-20 lg:py-28">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Certification Paths by ERP</h2>
            <p className="section-subheading mx-auto">
              Each ERP platform offers specific certifications. Our training prepares you for these exams.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(erpPrograms).map(([key, program], index) => (
              <div
                key={key}
                
                
                
                
              >
                <Card className="program-card h-full">
                  <CardContent className="pt-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${program.color} flex items-center justify-center text-2xl mb-4`}>
                      {program.icon}
                    </div>
                    <h3 className="font-heading font-bold text-lg text-brand-900 mb-4">{program.name} Certifications</h3>
                    <ul className="space-y-2 mb-4">
                      {program.certifications.map((cert, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <Award className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{cert}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={`/programs/${key}`} className="inline-flex items-center text-brand-600 text-sm font-medium hover:text-brand-700">
                      <span>View Program</span>
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Progression */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">ERP Career Progression</h2>
            <p className="section-subheading mx-auto">
              See how your career can grow with ERP skills and certifications
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {careerPaths.map((path, index) => (
              <div
                key={path.level}
                
                
                
                
              >
                <Card className="program-card h-full relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-full h-1 ${
                    index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-brand-500' : 'bg-accent'
                  }`} />
                  <CardContent className="pt-8">
                    <div className="flex items-center space-x-2 mb-4">
                      <TrendingUp className={`w-5 h-5 ${
                        index === 0 ? 'text-blue-500' : index === 1 ? 'text-brand-500' : 'text-accent'
                      }`} />
                      <span className="font-heading font-bold text-lg text-brand-900">{path.level}</span>
                    </div>
                    <div className="space-y-4 mb-6">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Experience Required</p>
                        <p className="font-medium text-brand-900">{path.experience}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Salary Range (US)</p>
                        <p className="font-mono font-medium text-brand-700 text-lg">{path.salary}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-2">Typical Roles</p>
                      <ul className="space-y-1">
                        {path.roles.map((role, i) => (
                          <li key={i} className="flex items-center space-x-2">
                            <Briefcase className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-700">{role}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exam Prep */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-heading mb-6">Our Exam Preparation Approach</h2>
              <p className="text-slate-600 mb-8">
                We don't just teach concepts - we prepare you specifically for certification exams 
                with targeted strategies and practice.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Target, title: 'Exam-Aligned Curriculum', desc: 'Content mapped to official exam objectives' },
                  { icon: BookOpen, title: 'Practice Tests', desc: 'Multiple mock exams with detailed explanations' },
                  { icon: Video, title: 'Exam Tips Sessions', desc: 'Strategy sessions from certified professionals' },
                  { icon: Users, title: 'Study Groups', desc: 'Collaborative learning with peers taking the same exam' },
                ].map((item, index) => {
                  const Icon = item.icon || Target;
                  return (
                    <div
                      key={item.title}
                      
                      
                      
                      
                      className="flex items-start space-x-4"
                    >
                      <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-brand-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-brand-900">{item.title}</h3>
                        <p className="text-sm text-slate-600">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-brand-50 rounded-2xl p-8">
              <div className="text-center">
                <div className="text-6xl font-bold text-brand-700 mb-2">95%</div>
                <p className="text-xl font-medium text-brand-900 mb-4">Certification Pass Rate</p>
                <p className="text-slate-600 mb-6">
                  Our students consistently achieve high pass rates on their first attempt.
                </p>
                <Link to="/programs">
                  <Button className="btn-primary">
                    Start Preparing Today
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Roles */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Job Roles You Can Pursue</h2>
            <p className="section-subheading mx-auto">
              ERP professionals are in demand across industries. Here are common roles our graduates pursue.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              'ERP Consultant', 'Functional Analyst', 'Technical Consultant', 'Solution Architect',
              'Implementation Specialist', 'Business Analyst', 'Project Manager', 'Support Analyst',
              'Integration Specialist', 'Data Migration Analyst', 'Training Coordinator', 'Pre-Sales Consultant'
            ].map((role, index) => (
              <div
                key={role}
                
                
                
                
                className="bg-white border border-slate-100 rounded-lg px-4 py-3 text-center hover:border-brand-200 hover:shadow-sm transition-all"
              >
                <span className="text-slate-700">{role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-brand-700">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Launch Your ERP Career?
            </h2>
            <p className="text-lg text-brand-100 mb-8">
              Start with a free career consultation to discuss your goals and the best certification path for you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-full px-8 py-3 font-medium" data-testid="career-consult-btn">
                  Book Career Consultation
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  View Programs & Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
